import { MetaResponseSchema, type MetaResponse } from "@legacy-manager/shared";
import { useEffect, useState, type ReactNode } from "react";

import { numero, useT } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import { PerfilSchema } from "../online/esquemas";
import { estadoDaPermissao, registrarPush } from "../online/push";
import { mensagemDoErro, useSessao, type DadosDaConta } from "../online/sessao";
import { iniciarRelatorioDeErros, relatorioDeErrosConfigurado } from "../erros";
import { definirPreferencia, usePreferencia } from "../preferencias";
import { Panel, Stat } from "../ui/atoms";
import { TEMAS, useTema, type Tema } from "../ui/tema";
import { Pagina } from "../ui/Pagina";
import { SeletorDeIdioma } from "../ui/SeletorDeIdioma";

/**
 * Opções: onde moram as escolhas que a pessoa vai refazer.
 *
 * O critério de entrada é um só, e ele é o que impede esta tela de virar
 * depósito: **entra o que alguém troca mais de uma vez na vida, ou o que
 * alguém precisa poder recusar.** Escolha única de instalação não entra, e
 * ajuste de partida não entra, porque ajuste de partida mora na partida.
 *
 * Por isso a tela não tem nada de tática, de velocidade de simulação nem de
 * tema: os dois primeiros pertencem ao hub, onde se joga, e o terceiro o
 * sistema operacional já decide por todo mundo.
 *
 * **Sem conta ela continua servindo.** Idioma, relatório de erros e "sobre"
 * não dependem de sessão nenhuma, e é de propósito: jogar sozinho não exige
 * cadastro (docs/LEGAL.md 0), então a tela onde se recusa o envio de dados não
 * pode exigir. O que depende do servidor aparece com o motivo escrito, nunca
 * como um botão apagado que ninguém sabe por que não clica.
 */

export function Opcoes({
  aoVoltar,
  aoSairDaCarreira,
}: {
  aoVoltar?: () => void;
  /** Só dentro de uma carreira: abre o diálogo de sair. */
  aoSairDaCarreira?: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  /*
   * A conta abre por cima, em vez de a tela copiar o que ela faz.
   *
   * Exportar e excluir são operações que já existem inteiras em `Conta.tsx`,
   * com o texto do que sai, o que fica e a palavra de confirmação. Repetir os
   * botões aqui criaria duas redações para o mesmo pedido, e a hora de elas
   * discordarem seria justo a hora de alguém apagar a própria conta.
   */

  return (
    <Pagina titulo={t("opcoes.titulo")} aoVoltar={aoVoltar}>
      {aoSairDaCarreira && (
        <Panel title={t("opcoes.carreira")}>
          <button className="ghost" onClick={aoSairDaCarreira}>
            {t("saida.titulo")}
          </button>
        </Panel>
      )}
      <PainelDeTema />
      <PainelDeIdioma />
      <PainelDeSom />
      <PainelDeAvisos conta={sessao.conta} />
      <PainelDeErros />
      <PainelSobre />
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Tema
// ---------------------------------------------------------------------------

/**
 * Claro, escuro ou o do clube.
 *
 * Os três botões mostram a cor que entregam, e não só o nome: o de escuro vem
 * escuro, o de claro vem claro, e o do brasão vem na cor do clube aberto. Tema
 * é escolha que se faz pelo olho, e um botão que diz "Brasão" sem mostrar qual
 * cor é obriga a experimentar para descobrir.
 */
function PainelDeTema(): ReactNode {
  const t = useT();
  const { tema, definirTema, brasaoDisponivel } = useTema();

  const amostra: Record<Tema, { fundo: string; marca: string }> = {
    escuro: { fundo: "#0d120f", marca: "#2fe06a" },
    claro: { fundo: "#ffffff", marca: "#14a04a" },
    /* O do brasão mostra a cor que está valendo agora, e por isso lê a
       variável em vez de trazer um valor fixo. */
    brasao: { fundo: "var(--surface-2)", marca: "var(--primary)" },
  };

  return (
    <Panel title={t("opcoes.tema.titulo")}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        {TEMAS.map((qual) => {
          const desligado = qual === "brasao" && !brasaoDisponivel;
          return (
            <button
              key={qual}
              className="ghost"
              aria-pressed={tema === qual}
              disabled={desligado}
              onClick={() => definirTema(qual)}
              style={{
                gap: 8,
                ...(tema === qual
                  ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                  : undefined),
              }}
            >
              <span
                aria-hidden
                className="fixo"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: amostra[qual].fundo,
                  boxShadow: `inset 0 0 0 4px ${amostra[qual].marca}`,
                  border: "1px solid var(--border-strong)",
                }}
              />
              {t(`opcoes.tema.${qual}`)}
            </button>
          );
        })}
      </div>

      {!brasaoDisponivel && (
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
          {t("opcoes.tema.semClube")}
        </p>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Idioma
// ---------------------------------------------------------------------------

/**
 * O mesmo seletor da tela inicial, e não outro.
 *
 * Um segundo controle seria um segundo estado da mesma escolha, com a chance de
 * os dois discordarem. O componente já grava no aparelho e já sobe o `locale`
 * para a conta; aqui ele só ganha um segundo endereço, que é o endereço que
 * quem procura "onde troco o idioma" vai abrir.
 */
function PainelDeIdioma(): ReactNode {
  const t = useT();
  return (
    <Panel title={t("opcoes.idioma.titulo")}>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 11.5 }}>
        {t("opcoes.idioma.ajuda")}
      </p>
      <SeletorDeIdioma />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Avisos
// ---------------------------------------------------------------------------

/**
 * Tipos de aviso que a tela oferece, na ordem em que eles importam.
 *
 * A lista é fixa aqui em vez de sair do que o servidor devolveu porque o
 * dicionário precisa ter um nome e uma explicação para cada uma, e uma chave
 * nova inventada do outro lado apareceria como caminho de tradução na tela.
 * Preferência que o servidor conheça e esta lista não simplesmente não é
 * mostrada, e continua valendo com o valor que ela já tinha.
 *
 * São estas cinco e não outras: são as que o servidor conhece
 * (`NOTIFICACOES_PADRAO` em `modules/users/service.ts`). Convite de liga e
 * convocação de seleção viram notificação, mas não têm preferência associada
 * do outro lado, então uma caixinha para elas aqui não desligaria nada.
 */
const TIPOS_DE_AVISO = ["deadline", "roundProcessed", "offers", "chat", "marketing"] as const;

/**
 * Quais avisos a pessoa quer, e o estado deste aparelho.
 *
 * As duas coisas moram no mesmo painel porque elas se explicam uma à outra:
 * marcar "rodada processada" não adianta nada se o navegador está bloqueando os
 * avisos, e é aqui que dá para descobrir isso sem ir caçar em configuração de
 * site.
 *
 * Cada troca vai sozinha para o servidor. `PATCH /v1/me` mistura o que chega com
 * o que já existia, então mandar só o que mudou é o suficiente, e nada se perde
 * se duas telas mexerem nisso ao mesmo tempo.
 */
function PainelDeAvisos({ conta }: { conta: DadosDaConta | null }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [permissao, definirPermissao] = useState(() => estadoDaPermissao());
  const [pedindo, definirPedindo] = useState(false);
  const [ocupado, definirOcupado] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);

  const trocar = async (tipo: string, ligado: boolean): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado(true);
    try {
      const perfil = await sessao.cliente.pedir(
        {
          metodo: "PATCH",
          caminho: "/v1/me",
          corpo: { notificationPrefs: { [tipo]: ligado } },
        },
        PerfilSchema,
      );
      sessao.aplicarPerfil(perfil);
      definirAviso(t("notificacoes.salvo"));
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  };

  const ligarNesteAparelho = async (): Promise<void> => {
    definirPedindo(true);
    await registrarPush(sessao.cliente, { pedirPermissao: true });
    definirPermissao(estadoDaPermissao());
    definirPedindo(false);
  };

  return (
    <Panel title={t("notificacoes.titulo")}>
      <p className="muted" style={{ margin: "0 0 12px", fontSize: 11.5 }}>
        {t("notificacoes.ajuda")}
      </p>

      {/* Sem conta não há preferência para guardar: elas moram no servidor, uma
          por pessoa. Dizer isso vale mais do que cinco caixinhas apagadas. */}
      {!conta ? (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {t("opcoes.avisos.precisaDeConta")}
        </p>
      ) : (
        <>
          {aviso && (
            <p className="label" style={{ margin: "0 0 10px" }}>
              {aviso}
            </p>
          )}

          <div className="grid" style={{ gap: 10 }}>
            {TIPOS_DE_AVISO.filter((tipo) => tipo in conta.notificacoes).map((tipo) => (
              <label key={tipo} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
                <input
                  type="checkbox"
                  /* Só `false` desliga. Preferência que o servidor ainda não conhece
                     nasce ligada, que é o padrão dele. */
                  checked={conta.notificacoes[tipo] !== false}
                  disabled={ocupado}
                  onChange={(evento) => void trocar(tipo, evento.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600 }}>{t(`notificacoes.tipos.${tipo}`)}</span>
                  <span className="muted" style={{ display: "block", fontSize: 11.5 }}>
                    {t(`notificacoes.tiposAjuda.${tipo}`)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </>
      )}

      {/* O bloco do aparelho só existe com conta: registrar o token deste
          navegador é pedido autenticado, e gastar o único pedido de permissão
          da vida deste navegador sem ter onde guardar o token o queimaria. */}
      <div
        hidden={!conta}
        style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}
      >
        {permissao === "concedida" && (
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            {t("notificacoes.jaAtivos")}
          </p>
        )}
        {permissao === "negada" && (
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            {t("notificacoes.bloqueados")}
          </p>
        )}
        {permissao === "indisponivel" && (
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            {t("notificacoes.indisponiveis")}
          </p>
        )}
        {permissao === "perguntar" && (
          <button className="ghost" disabled={pedindo} onClick={() => void ligarNesteAparelho()}>
            {pedindo ? t("notificacoes.convite.pedindo") : t("notificacoes.convite.sim")}
          </button>
        )}
      </div>
    </Panel>
  );
}


// ---------------------------------------------------------------------------
// Som
// ---------------------------------------------------------------------------

function PainelDeSom(): ReactNode {
  const t = useT();
  const ligado = usePreferencia("somDoJogo");
  return (
    <Panel title={t("opcoes.som.titulo")}>
      <label className="row" style={{ gap: 10 }}>
        <input
          type="checkbox"
          role="switch"
          checked={ligado}
          onChange={(evento) => definirPreferencia("somDoJogo", evento.target.checked)}
        />
        <span style={{ fontWeight: 600 }}>{t("opcoes.som.gol")}</span>
      </label>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Relatório de erros
// ---------------------------------------------------------------------------

/**
 * A recusa, que antes não existia.
 *
 * Um defeito que só aparece na rodada quarenta chega por relatório automático
 * ou não chega, e é por isso que o relatório existe. Mas `docs/LEGAL.md` 1
 * promete que o modo solo não manda nada para servidor nenhum e fala em "se
 * você ativar", então o padrão é desligado e esta caixinha é onde alguém
 * ativa. Promessa escrita vale mais do que relatório a mais.
 *
 * O texto diz o que sobe antes de pedir o sim, e diz a parte que mais importa:
 * o save nunca sobe. Consentimento sem saber a que é assinatura em branco.
 */
function PainelDeErros(): ReactNode {
  const t = useT();
  const ligado = usePreferencia("relatorioDeErros");
  const configurado = relatorioDeErrosConfigurado();

  const trocar = (marcado: boolean): void => {
    definirPreferencia("relatorioDeErros", marcado);
    // Ligar tem que valer para o erro de agora. Desligar já valeu na linha
    // acima, porque os filtros do SDK releem a escolha a cada evento.
    if (marcado) iniciarRelatorioDeErros();
  };

  return (
    <Panel title={t("opcoes.erros.titulo")}>
      <label className="row" style={{ gap: 10, alignItems: "flex-start" }}>
        <input
          type="checkbox"
          checked={ligado}
          onChange={(evento) => trocar(evento.target.checked)}
          style={{ marginTop: 3 }}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>{t("opcoes.erros.enviar")}</span>
          <span className="muted" style={{ display: "block", fontSize: 11.5 }}>
            {t("opcoes.erros.oQueVai")}
          </span>
        </span>
      </label>

      {/* Destaque sem caixa alta: `.label` é para rótulo de duas palavras, e uma
          frase inteira em maiúscula é justamente a que ninguém lê. */}
      <p
        style={{
          margin: "12px 0 0",
          paddingLeft: 10,
          borderLeft: "2px solid var(--primary)",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {t("opcoes.erros.saveNuncaVai")}
      </p>

      {/* Sem endereço configurado na build, marcar a caixa não faria nada. Dizer
          isso é melhor do que deixar alguém achar que está ajudando. */}
      {!configurado && (
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
          {t("opcoes.erros.semEndereco")}
        </p>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Dados e conta
// ---------------------------------------------------------------------------
// Sobre
// ---------------------------------------------------------------------------

/**
 * O que o jogo carrega junto, e sob que licença.
 *
 * A lista mora no código e não no dicionário porque nome de biblioteca e
 * identificador de licença não se traduzem: passá-los pelo i18n criaria três
 * cópias iguais da mesma linha, e um dia duas delas ficariam para trás.
 */
const BIBLIOTECAS: ReadonlyArray<{ nome: string; licenca: string }> = [
  { nome: "React", licenca: "MIT" },
  { nome: "Zod", licenca: "MIT" },
  { nome: "Sentry", licenca: "MIT" },
  { nome: "Tauri", licenca: "MIT OR Apache-2.0" },
  { nome: "Capacitor", licenca: "MIT" },
  { nome: "Workbox", licenca: "MIT" },
  { nome: "Vite", licenca: "MIT" },
];

/**
 * Versão do jogo, versão do motor e de onde vieram.
 *
 * As duas primeiras saem da build e aparecem sem rede nenhuma: alguém que veio
 * relatar um defeito precisa dos números justamente quando o servidor não
 * responde. O que vem de `GET /v1/meta` é o que está do outro lado, e some em
 * silêncio quando não dá para perguntar, em vez de virar erro vermelho numa
 * tela que não estava pedindo nada.
 */
function PainelSobre(): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [meta, definirMeta] = useState<MetaResponse | null>(null);

  const cliente = sessao.cliente;

  useEffect(() => {
    let vivo = true;
    void cliente
      .pedir({ metodo: "GET", caminho: "/v1/meta", publica: true }, MetaResponseSchema)
      .then((lido) => {
        if (vivo) definirMeta(lido);
      })
      .catch(() => {
        // Jogo aberto sem rede, ou sem servidor configurado. As versões da
        // build continuam na tela, que é o que alguém veio buscar aqui.
      });
    return () => {
      vivo = false;
    };
  }, [cliente]);

  return (
    <Panel title={t("opcoes.sobre.titulo")}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}
      >
        <Stat label={t("opcoes.sobre.versaoDoJogo")}>
          <span className="num">{import.meta.env.VITE_APP_VERSION ?? "0.1.0"}</span>
        </Stat>
        <Stat label={t("opcoes.sobre.versaoDoMotor")}>
          <span className="num">{import.meta.env.VITE_ENGINE_VERSION ?? "0.1.0"}</span>
        </Stat>
        {meta && (
          <>
            <Stat label={t("opcoes.sobre.versaoDaApi")}>
              <span className="num">{meta.apiVersion}</span>
            </Stat>
            <Stat label={t("opcoes.sobre.protocolo")}>
              <span className="num">{numero(meta.protocolVersion)}</span>
            </Stat>
          </>
        )}
      </div>

      {/* O motor do servidor é o que vale numa liga online, e ele pode estar à
          frente do desta build. Só aparece quando os dois discordam: repetir o
          mesmo número duas vezes não informa nada. */}
      {meta && meta.engineVersion !== (import.meta.env.VITE_ENGINE_VERSION ?? "0.1.0") && (
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
          {t("opcoes.sobre.motorDoServidor", { versao: meta.engineVersion })}
        </p>
      )}

      <details style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
        <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 600 }}>
          {t("opcoes.sobre.licencas")}
        </summary>
        <p className="muted" style={{ margin: "10px 0", fontSize: 11.5 }}>
          {t("opcoes.sobre.licencaDoJogo")}
        </p>
        <div className="grid" style={{ gap: 4 }}>
          {BIBLIOTECAS.map((biblioteca) => (
            <div key={biblioteca.nome} className="row" style={{ gap: 10, fontSize: 11.5 }}>
              <span style={{ flex: 1, minWidth: 0 }}>{biblioteca.nome}</span>
              <span className="muted">{biblioteca.licenca}</span>
            </div>
          ))}
        </div>
      </details>
    </Panel>
  );
}
